import { z } from 'zod';

import {
  type SttConfigModel,
  sttConfigModelSchema,
  sttConfigModelToJson,
} from '@/domain/models/sttConfigModel';
import { type Nullable, resolveNullable } from '@/utils/nullable';

/** `characterType` по умолчанию: старый бекенд поле не отдаёт. */
const DEFAULT_CHARACTER_TYPE = 'web';

/** Тип персонажа, который приложение не поддерживает. */
const UNREAL_CHARACTER_TYPE = 'unreal';

export interface PersonModel {
  readonly id: number;
  readonly slug: string;
  readonly collectionName: string;
  readonly picture: string;
  readonly completionUrl: string;
  readonly personBaseUrl: string;
  readonly giff: string | null;
  readonly name: string | null;
  readonly extraParams: Record<string, unknown> | null;

  /** Распознавание речи этого аватара; `null` — берётся общий `transcribe_url`. */
  readonly stt: SttConfigModel | null;

  /**
   * `web` (LiveTalking) или `unreal`. Старый бекенд поле не отдаёт —
   * там все персонажи web.
   */
  readonly characterType: string;
}

/** snake_case из сети переводится в camelCase в `.transform()` (см. sttConfigModel). */
export const personModelSchema = z
  .object({
    id: z.number(),
    slug: z.string(),
    collection_name: z.string(),
    picture: z.string(),
    completion_url: z.string(),
    person_base_url: z.string(),
    giff: z.string().nullish(),
    name: z.string().nullish(),
    extra_params: z.record(z.string(), z.unknown()).nullish(),
    stt: sttConfigModelSchema.nullish(),
    character_type: z.string().nullish(),
  })
  .transform(
    (json): PersonModel => ({
      // `(json['id'] as num).toInt()` — Dart усекает дробную часть.
      id: Math.trunc(json.id),
      slug: json.slug,
      collectionName: json.collection_name,
      picture: json.picture,
      completionUrl: json.completion_url,
      personBaseUrl: json.person_base_url,
      giff: json.giff ?? null,
      name: json.name ?? null,
      extraParams: json.extra_params ?? null,
      stt: json.stt ?? null,
      characterType: json.character_type ?? DEFAULT_CHARACTER_TYPE,
    }),
  );

export function personModelFromJson(json: unknown): PersonModel {
  return personModelSchema.parse(json);
}

export function personModelToJson(model: PersonModel): Record<string, unknown> {
  return {
    id: model.id,
    slug: model.slug,
    collection_name: model.collectionName,
    picture: model.picture,
    completion_url: model.completionUrl,
    person_base_url: model.personBaseUrl,
    giff: model.giff,
    name: model.name,
    extra_params: model.extraParams,
    stt: model.stt === null ? null : sttConfigModelToJson(model.stt),
    character_type: model.characterType,
  };
}

/** Unreal-персонажей приложение не поддерживает — они скрываются из списка. */
export function isUnrealPerson(person: PersonModel): boolean {
  return person.characterType === UNREAL_CHARACTER_TYPE;
}

/**
 * Патч для [personCopyWith]. Обнуляемые поля идут в `Nullable`: без обёртки
 * поле не трогается, `new Nullable(null)` стирает его — как sentinel
 * `freezed`, на который опирается слияние STT в `tablet_session_manager`.
 */
export interface PersonModelPatch {
  readonly id?: number;
  readonly slug?: string;
  readonly collectionName?: string;
  readonly picture?: string;
  readonly completionUrl?: string;
  readonly personBaseUrl?: string;
  readonly giff?: Nullable<string | null>;
  readonly name?: Nullable<string | null>;
  readonly extraParams?: Nullable<Record<string, unknown> | null>;
  readonly stt?: Nullable<SttConfigModel | null>;
  readonly characterType?: string;
}

/** `PersonModel.copyWith` — всегда новый объект, исходный не меняется. */
export function personCopyWith(
  person: PersonModel,
  patch: PersonModelPatch,
): PersonModel {
  return {
    id: patch.id ?? person.id,
    slug: patch.slug ?? person.slug,
    collectionName: patch.collectionName ?? person.collectionName,
    picture: patch.picture ?? person.picture,
    completionUrl: patch.completionUrl ?? person.completionUrl,
    personBaseUrl: patch.personBaseUrl ?? person.personBaseUrl,
    giff: resolveNullable(patch.giff, person.giff),
    name: resolveNullable(patch.name, person.name),
    extraParams: resolveNullable(patch.extraParams, person.extraParams),
    stt: resolveNullable(patch.stt, person.stt),
    characterType: patch.characterType ?? person.characterType,
  };
}
